import { yupResolver } from "@hookform/resolvers/yup";
import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  useUpdateEtudeDataMutation,
  useUpdateEtudeNameMutation,
  useUpdateProjectTraceMutation,
} from "store/api";
import { setEtudeData, setEtudeId } from "store/etudeSlice";
import { useAppSelector } from "store/hooks";
import { SubAppCode } from "utils/engine/consts";
import useCustomForm from "utils/hooks/useCustomForm";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import * as yup from "yup";
import { InstallationType } from "../EtudeForm/hooks/useGetSolutionsQuery";
import Loader from "components/shared/loader";
import { formatDateTime } from "utils";
import { Text } from "components/shared/text";
import { ProjectTrace } from "types/ProjectTrace";
import MapValuesToProjectTrace from "utils/helpers/projectTrace";
import {
  getLocalStorageTraceId,
  setLocalStorageTraceId,
} from "utils/helpers/localStorage";

const defaultValues = {
  id: 0,
  thercodim_projectId: 0,
  salesnetworkId: 0,
  collaborateurId: 0,
  reference: null,
  siteName: null,
  siteAdress: "",
  siteCP: null,
  siteCity: null,
  clientName: null,
  clientContact: "",
  clientAdress: "",
  clientCP: "",
  clientCity: "",
  clientMail: "",
  clientPhoneNumber: "",
  version: 0,
  json: null,
  definition: null,
  numero: null,
  subapplication: null,
  application: null,
  system: null,
  key: null,
  createdAt: "",
  updatedAt: "",
};
export type LocationState = {
  solutions: Solution[];
  type?: InstallationType;
  appliedSolutionFilter?: GetSolutionRequest | null;
  etudeData?: any;
  service?: string;
};
export const SavePopup: React.FC<{
  closePopup: any;
  application?: string;
  subApplication?: (typeof SubAppCode)[keyof typeof SubAppCode] | null;
  systeme?: string;
  formData?: any;
  callback?: (id: number) => void;
  saveLocally?: boolean;
  isRenaming?: boolean;
  renamingEtudeId?: any;
  currentName?: string;
  refetchEtudeListData?: any;
}> = ({
  closePopup,
  application,
  subApplication,
  systeme,
  formData,
  callback,
  saveLocally,
  isRenaming = false,
  renamingEtudeId,
  currentName,
  refetchEtudeListData,
}) => {
    const { etudeId, etudeType } = useParams();

    const { t } = useTranslation();
    const { network } = useSalesNetwork();
    const { etudeData, noSolution } = useAppSelector((state) => state.etude);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [updateEtude, { isSuccess, isLoading }] = useUpdateEtudeDataMutation();
    const [updateEtudeName] = useUpdateEtudeNameMutation();
    const [updateProjectTrace] = useUpdateProjectTraceMutation();
    const schema = yup.object({
      siteName: yup
        .string()
        .max(80)
        .required(t("ERRMESS_siteName"))
        .notLongWord(),
      siteCP: yup.string().max(20).required(t("ERRMESS_siteCP")).notLongWord(),
      siteCity: yup
        .string()
        .max(30)
        .required(t("ERRMESS_siteCity"))
        .notLongWord(),
      clientName: yup
        .string()
        .max(50)
        .required(t("ERRMESS_clientName"))
        .notLongWord(),
      siteAdress: yup.string().nullable().max(50).notLongWord(),
      reference: yup.string().nullable().max(20).notLongWord(),
      clientContact: yup.string().nullable().max(50).notLongWord(),
      clientAdress: yup.string().nullable().max(50).notLongWord(),
      clientCP: yup.string().nullable().max(20).notLongWord(),
      clientCity: yup.string().nullable().max(30).notLongWord(),
      clientMail: yup.string().nullable().max(50).notLongWord(),
      clientPhoneNumber: yup.string().nullable().max(15).notLongWord(),
    });

    const { handleSubmit, reset, watch, control, getValues } =
      useCustomForm<GetEtudeResponse>({
        resolver: yupResolver(schema) as any,
        defaultValues: (isRenaming
          ? { siteName: currentName || "" }
          : defaultValues) as any,
        mode: "all",
      });

    useEffect(() => {
      if (isRenaming && currentName) {
        reset({ siteName: currentName || "" });
      } else if (etudeId && etudeData) {
        const mappedData = {} as GetEtudeResponse;
        for (let key in defaultValues) {
          if (defaultValues.hasOwnProperty(key)) {
            mappedData[key as keyof GetEtudeResponse] = (
              etudeData as GetEtudeResponse
            )[key];
          }
        }
        reset(mappedData);
      }
    }, [etudeData, currentName, isRenaming, etudeId, reset]);

    const handleUpdate = async (data: any) => {
      const subApplicationItem = network?.services
        ?.find((s) => s.code == etudeType)
        ?.applications?.find((s) => s.code == application)
        ?.subApplications?.find((s) => s.code == subApplication);

      const subApplicationId = subApplicationItem?.subApplicationId;
      const systemId = subApplicationItem?.systems?.find(
        (s) => s.code == systeme
      )?.systemId;

      const etudeUpdateData: GetEtudeResponse = {
        ...defaultValues,
        ...data,
        application,
        subApplication,
        subApplicationId,
        system: systeme,
        systemId,
        service: etudeType,
        json: JSON.stringify(formData),
      };

      delete etudeUpdateData.createdAt;
      delete etudeUpdateData.updatedAt;

      if (saveLocally) {
        dispatch(setEtudeData({ ...etudeData, ...etudeUpdateData }));
        callback?.(etudeId as unknown as number);
        closePopup();
      } else {
        return updateEtude({
          etudeDto: etudeUpdateData,
          code: network?.code,
        }).then(async (result: any) => {
          if (result.error) {
            throw new Error(result.error.data.title);
          }
          etudeUpdateData.numero = result?.data?.numero;
          etudeUpdateData.version = result?.data?.version;
          etudeUpdateData.salesnetworkId = result?.data?.salesnetworkId;
          etudeUpdateData.collaborateurId = result?.data?.collaborateurId;

          let newPath = location.pathname.replace(":etudeId", result?.data?.id);
          if (etudeId) {
            newPath = location.pathname.replace(
              new RegExp(etudeId, "g"),
              result?.data?.id
            );
          }

          // newPath = getAppUrl(
          //   `/etude/${etudeType}/${result?.data?.thercodim_projectId}`
          // );

          // Navigate to the updated path

          dispatch(setEtudeId(result?.data?.id));
          dispatch(
            setEtudeData({
              ...result?.data,
              etudeId: result?.data?.id,
            })
          );
          reset(result);

          navigate(newPath, {
            replace: true,
          });

          let id = getLocalStorageTraceId();

          let projectTrace: ProjectTrace = MapValuesToProjectTrace(
            {
              Id: id ? +id : 0,
              network: network?.code,
              service: etudeType,
              ...formData,
              etudeData: result?.data,
              saved: true,
              noSolution: noSolution,
            },
            t
          );

          let project = await updateProjectTrace(projectTrace);

          if (project?.data?.id) {
            setLocalStorageTraceId(project.data.id);
          }

          return false; // displayMessage
          // const dateTime = new Date("2025-01-29T10:39:51.177");
          // const dateOnly = dateTime.toLocaleDateString("en-CA"); // "YYYY-MM-DD" format
        });
      }
    };

    const handleUpdateEtudeName = () => {
      updateEtudeName({
        etudeId: renamingEtudeId,
        name: watch("siteName"),
      })
        .then(() => {
          refetchEtudeListData();
        })
        .finally(() => {
          closePopup();
        });
    };

    return (
      <>
        {!isSuccess ? (
          <Popup
            title={t("FO_SAVE_STUDY")}
            translateTitle
            fixSize={isRenaming ? "small" : "medium"}
          >
            {isLoading && <Loader />}
            <group
              data-direction="column"
              data-gap="20"
              data-space="20"
              data-height="autofit"
            >
              <form data-type="group">
                <group data-gap="10">
                  <group
                    data-direction="column"
                    data-gap="10"
                    data-length="autofit"
                  >
                    {!isRenaming && (
                      <Text data-space-horizontal="5">
                        {t("FO_DOC_PROJET_CHANTIER")}
                      </Text>
                    )}
                    <Input
                      dataLength=""
                      labelSize={12}
                      label={`${t("FO_DOC_SITENAME")}*`}
                      maxLength={80}
                      control={control}
                      name="siteName"
                      type="text"
                    />
                    {!isRenaming && (
                      <>
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_REFERENCE")}
                          maxLength={20}
                          control={control}
                          name="reference"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_SITEADRESS")}
                          maxLength={50}
                          control={control}
                          name="siteAdress"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={`${t("FO_DOC_SITECP")}*`}
                          control={control}
                          name="siteCP"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={`${t("FO_DOC_SITECITY")}*`}
                          control={control}
                          maxLength={30}
                          name="siteCity"
                          type="text"
                        />
                      </>
                    )}
                  </group>
                  {!isRenaming && (
                    <>
                      <group
                        data-direction="column"
                        data-gap="10"
                        data-length="autofit"
                      >
                        <Text data-space-horizontal="5">
                          {t("FO_DOC_CLIENT")}
                        </Text>
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={`${t("FO_DOC_CLIENTNAME")}*`}
                          maxLength={50}
                          control={control}
                          name="clientName"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTCONTACT")}
                          maxLength={50}
                          control={control}
                          name="clientContact"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTADRESS")}
                          maxLength={50}
                          control={control}
                          name="clientAdress"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTCP")}
                          control={control}
                          name="clientCP"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTCITY")}
                          control={control}
                          name="clientCity"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTCMAIL")}
                          control={control}
                          name="clientMail"
                          type="text"
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_CLIENTPHONENUMBER")}
                          control={control}
                          name="clientPhoneNumber"
                          type="text"
                        />
                      </group>
                      <group
                        data-direction="column"
                        data-length="autofit"
                        data-gap="10"
                      >
                        <Text data-space-horizontal="5">{t("FO_DOC_ETUDE")}</Text>

                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_ID")}
                          control={control}
                          name="numero"
                          type="text"
                          disabled
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_VERSION")}
                          control={control}
                          name="version"
                          type="text"
                          disabled
                        />
                        <Input
                          dataLength=""
                          labelSize={12}
                          label={t("FO_DOC_DATE")}
                          control={control}
                          name="updatedAtFormatted"
                          value={formatDateTime(getValues("updatedAt"))}
                          //name={EtudeData?.updatedAt ? "updatedAt" : "createdAt"}
                          type="text"
                          disabled
                        />
                      </group>
                    </>
                  )}
                </group>
              </form>
              <group data-justify="end" data-gap="10" data-position="bottom">
                <Button
                  accent
                  large
                  onClick={() => {
                    closePopup();
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  primary
                  large
                  onClick={() => {
                    isRenaming
                      ? handleUpdateEtudeName()
                      : handleSubmit(
                        handleUpdate,
                        () => { },
                        (error) => {
                          console.log(error);
                        }
                      )();
                  }}
                >
                  {t("save")}
                </Button>
              </group>
            </group>
          </Popup>
        ) : (
          <Popup title="Enregistrer l'etude" translateTitle fixSize="small">
            <group
              data-direction="column"
              data-gap="20"
              data-space="20"
              data-height="autofit"
              data-length="450"
            >
              <group data-space="50" data-fit="1">
                <group
                  data-space="10"
                  data-radius="30"
                  data-background="success-color-light"
                  data-width="auto"
                  data-position="center"
                >
                  <icon data-icon-size="medium" data-color="success-color-dark">
                    done
                  </icon>
                </group>
              </group>
              <Button
                accent
                large
                //data-length="forcefit"
                //data-fit="1"
                onClick={() => {
                  !!etudeId &&
                    !isNaN(Number(etudeId)) &&
                    callback?.(etudeId as unknown as number);
                  closePopup();
                }}
              >
                {t("FO_BUTTON_CLOSE")}
              </Button>
            </group>
          </Popup>
        )}
      </>
    );
  };
